import { useSharedValue } from 'react-native-reanimated';
import { View, StyleSheet, Keyboard } from 'react-native';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { IconBoldArrow } from 'design-system/icons';
import DepositAmountSelector from 'design-system/DepositAmountSelector';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { rem, AppView, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectActiveTradingSecclAccountsLength, selectInitialToTradingAccount, selectTradingAccountFromId, } from 'features/invest/reducers/selectors';
import { POT_DEPOSIT_AMOUNT_OPTIONS } from 'features/pots/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import TransferSecclRow from 'features/transfers/components/TransferSecclRow';
import SecclAccountRow from '../components/SecclAccountRow';
import { selectConnectedAccountBalanceFromConnectedAccountWithBankId, useConnectedAccount } from '../hooks/selectors';
import { TradingAccountToMoveMoneyToPickerModal } from '../components/TradingAccountToMoveMoneyToPickerModal';
import SelectDepositToInvestmentAccountMethodModal from '../components/SelectDepositToInvestmentAccountMethodModal';
import ConnectedAccountRow from '../components/ConnectedAccountRow';
import { TradingAccountDepositMethodPickerModal } from '../components/TradingAccountDepositMethodPickerModal';
import useInvestCashToTradingAccount from '../hooks/useInvestCashToTradingAccount';
import { INVEST_MAXIMUM_DD_DEPOSIT_LIMIT, INVEST_MAXIMUM_OB_DEPOSIT_LIMIT } from '../constants';
import { isWeb } from '../../../constants';
const strings = {
    title: 'Deposit',
    continue: 'Continue',
    minAmountErrorPrefix: 'Minimum amount is ',
    maxAmountErrorSuffix: ' maximum',
    exceedBalanceError: 'Your balance is too low',
    leftToInvest: ' left to invest this tax year',
};
const AddMoneyToTradingAccount = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const tradingAccountIdFromRoute = route.params?.tradingAccountId;
    const from = useConnectedAccount();
    const initialToTradingAccount = useAppSelector(selectInitialToTradingAccount);
    const tradingAccountFromRoute = useAppSelector((state) => selectTradingAccountFromId(state, tradingAccountIdFromRoute));
    const tradingAccount = tradingAccountFromRoute || initialToTradingAccount;
    const activeTradingAccountsLen = useAppSelector(selectActiveTradingSecclAccountsLength);
    const hasMoreActiveTradingAccounts = activeTradingAccountsLen > 1;
    const [isToPickerOpen, setIsToPickerOpen] = useState(false);
    const [isFromPickerOpen, setIsFromPickerOpen] = useState(false);
    const toPickerSharedValue = useSharedValue(0);
    const [to, setTo] = useState(tradingAccount);
    const [amountError, setAmountError] = useState();
    const [depositMethod, setDepositMethod] = useState(from?.pispBankId ? 'openBankingDeposit' : 'singleDirectDebit');
    const maxDepositLimit = depositMethod === 'singleDirectDebit' ? INVEST_MAXIMUM_DD_DEPOSIT_LIMIT : INVEST_MAXIMUM_OB_DEPOSIT_LIMIT;
    const investCashToTradingAccount = useInvestCashToTradingAccount(tradingAccountFromRoute);
    useEffect(() => {
        if (tradingAccountIdFromRoute && !tradingAccountFromRoute) {
            navigation.popToTop();
        }
    }, [tradingAccountFromRoute, tradingAccountIdFromRoute]);
    const remainingAllowance = useMemo(() => {
        if (!to)
            return undefined;
        // eslint-disable-next-line no-nested-ternary
        return to?.accountType === 'JISA'
            ? to?.jisaRemainingAllowanceAfterPending
            : to?.accountType === 'ISA'
                ? to?.isaRemainingAllowanceAfterPending
                : null;
    }, [to]);
    const connectedAccountBalance = useAppSelector(selectConnectedAccountBalanceFromConnectedAccountWithBankId);
    const fromBalance = useMemo(() => {
        if (from && 'ukAccountNumber' in from && depositMethod === 'openBankingDeposit') {
            return connectedAccountBalance;
        }
        return undefined;
    }, [connectedAccountBalance, depositMethod, from]);
    useEffect(() => {
        if (!to) {
            navigation.goBack();
        }
    }, [to]);
    const [amount, setAmount] = useState(() => {
        if (connectedAccountBalance && depositMethod === 'openBankingDeposit') {
            if (connectedAccountBalance.amount >= 100) {
                return 100;
            }
            if (connectedAccountBalance.amount >= 50) {
                return 50;
            }
            return 0;
        }
        return 100;
    });
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.title,
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary, navigation]);
    const showTradingAccountPicker = useCallback(() => {
        Keyboard.dismiss();
        setIsToPickerOpen(true);
        TradingAccountToMoveMoneyToPickerModal.show(to?.id, (selection) => {
            setTo(selection);
            setIsToPickerOpen(false);
        }, toPickerSharedValue, () => setIsToPickerOpen(false));
    }, [to?.id, toPickerSharedValue]);
    const onFromPress = useCallback(() => {
        Keyboard.dismiss();
        setIsFromPickerOpen(true);
        if (from) {
            TradingAccountDepositMethodPickerModal.show((method) => {
                if (method) {
                    setDepositMethod(method);
                }
                setIsFromPickerOpen(false);
            }, depositMethod, () => setIsFromPickerOpen(false));
        }
    }, [depositMethod, from]);
    const From = useMemo(() => {
        if (from) {
            return (<ConnectedAccountRow account={from} isPickerModalOpen={isFromPickerOpen} onPress={from.pispBankId ? onFromPress : undefined} description={depositMethod === 'openBankingDeposit' ? 'Open Banking transfer' : 'Direct Debit'}/>);
        }
        return null;
    }, [depositMethod, from, isFromPickerOpen, onFromPress]);
    const To = useMemo(() => {
        if (tradingAccountFromRoute) {
            return <TransferSecclRow useCashAsTitle/>;
        }
        if (to) {
            return (<SecclAccountRow tradingAccountId={to.id} isPickerModalOpen={isToPickerOpen} onPress={hasMoreActiveTradingAccounts && !tradingAccountFromRoute ? showTradingAccountPicker : undefined}/>);
        }
        return null;
    }, [hasMoreActiveTradingAccounts, isToPickerOpen, showTradingAccountPicker, to, tradingAccountFromRoute]);
    const checkAmountError = useCallback((value) => {
        if (value < 1) {
            setAmountError('belowMin');
        }
        else if (value > maxDepositLimit) {
            setAmountError('exceedMax');
        }
        else if (remainingAllowance != null && value > remainingAllowance.value) {
            setAmountError('exceedAllowance');
        }
        else if (fromBalance && value > fromBalance.amount) {
            setAmountError('exceedBalance');
        }
        else {
            setAmountError(undefined);
        }
    }, [maxDepositLimit, remainingAllowance, fromBalance]);
    useEffect(() => {
        if (amount) {
            checkAmountError(amount);
        }
        else {
            setAmountError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkAmountError]); // we want this hook to run only if checkAmountError updates
    const footnote = useMemo(() => {
        const isError = !!amountError;
        const colorProps = isError ? { Red: true } : { Secondary: true };
        let content;
        if (remainingAllowance) {
            content = (<>
              <Text NumbersThin-14 {...colorProps}>
                {formatAmount(remainingAllowance.value, remainingAllowance.currency, 2)}
              </Text>
              {strings.leftToInvest}
            </>);
        }
        if (amountError === 'belowMin') {
            content = (<>
              {strings.minAmountErrorPrefix}
              <Text NumbersThin-14 Red>
                £1
              </Text>
            </>);
        }
        else if (amountError === 'exceedMax') {
            content = (<>
              <Text NumbersThin-14 Red>
                {formatAmount(maxDepositLimit, 'GBP')}
              </Text>
              {strings.maxAmountErrorSuffix}
            </>);
        }
        else if (amountError === 'exceedBalance') {
            content = strings.exceedBalanceError;
        }
        if (!content)
            return undefined;
        return (<Text TextThin-14 {...colorProps}>
            {content}
          </Text>);
    }, [amountError, maxDepositLimit, remainingAllowance]);
    const handleAmountChange = useCallback((value) => {
        setAmount(value);
        checkAmountError(value);
    }, [checkAmountError]);
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        const numberAmount = amount ?? 0;
        if (to) {
            if (tradingAccountIdFromRoute) {
                investCashToTradingAccount({
                    amount: numberAmount,
                    depositMethod,
                });
            }
            else {
                SelectDepositToInvestmentAccountMethodModal.show(numberAmount, to, depositMethod);
            }
        }
    }, [amount, depositMethod, investCashToTradingAccount, to, tradingAccountIdFromRoute]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.contentContainer);
    return (<AppView withFrame={false}>
          <AnimatedKeyboardAwareScrollView enableOnAndroid onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
            {From}
            <Spacer h={16}/>
            <View>
              {To}
              <View style={styles.iconContainer} pointerEvents="none">
                <View style={styles.iconWrapper}>
                  <IconBoldArrow alt colorKey="brand" direction="bottom"/>
                </View>
              </View>
            </View>
            <Spacer h={16}/>
            <DepositAmountSelector min={1} amount={amount} footnote={footnote} max={maxDepositLimit} setAmount={handleAmountChange} options={POT_DEPOSIT_AMOUNT_OPTIONS} availableAmount={fromBalance?.amount} remainingAllowance={remainingAllowance?.value}/>
          </AnimatedKeyboardAwareScrollView>
          <KeyboardAvoidingButton brand title={strings.continue} onPress={handleContinue} disabled={!to || !from || !amount || !!amountError}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    title: strings.title,
}));
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        top: -rem(30),
    },
    iconWrapper: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(44),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.light,
    },
    contentContainer: {
        paddingTop: rem(16),
    },
}));
export default AddMoneyToTradingAccount;
